import {
  BiddingApprovalState,
  BiddingIntradayOrderStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

/**
 * 詳細モーダルに出す差戻しの見出しの種類 (AC-BIN-049 / AC-BIN-095)。
 * - `rejected`: 現在差し戻されている札 (REJECTED)
 * - `reopenedDraft`: 差戻し後再編集で下書きに戻した札 (DRAFT)
 * - `priorRejection`: 再申請して承認待ちになった札 (PENDING) に出す前回の差戻し
 */
export type IntradayRejectionNoticeKind =
  | 'rejected'
  | 'reopenedDraft'
  | 'priorRejection'

export type IntradayRejectionNotice = {
  kind: IntradayRejectionNoticeKind
  /** error は現在差し戻されている札だけ。 */
  status: 'error' | 'warning'
}

/**
 * 承認ステータスと差戻し履歴 (差戻し理由・日時のどちらかがあるか) から、 見出しの
 * 種類を決める。
 *
 * REJECTED は履歴の有無に依らず常に見出しを出す: 差戻しの札であること自体を伝える
 * 見出しで、 理由や日時を持たない旧データの差戻し札でも「差し戻された」 ことは
 * 分かる必要がある。 DRAFT / PENDING は「前回の差戻し」 を見せる見出しなので、
 * 履歴がある時だけ。 backend は承認・提出の後も rejected_* を消さないため、
 * それ以外の状態 (APPROVED / SUBMITTED ほか) では出さない。
 *
 * 約定軸が失効 (EXPIRED) の札には出さない: GC で失効にした札は承認ステータスが
 * N/A (DRAFT) に戻るが再編集も再申請もできず、 DRAFT の見出しでは再編集中と読めてしまう
 * (AC-BIN-099、 #11099)。
 */
export function resolveIntradayRejectionNotice(
  approvalState: BiddingApprovalState | undefined,
  hasRejectionHistory: boolean,
  bidStatus: BiddingIntradayOrderStatus | undefined,
): IntradayRejectionNotice | undefined {
  if (bidStatus === BiddingIntradayOrderStatus.EXPIRED) return undefined
  switch (approvalState) {
    case BiddingApprovalState.REJECTED:
      return { kind: 'rejected', status: 'error' }
    case BiddingApprovalState.DRAFT:
      return hasRejectionHistory
        ? { kind: 'reopenedDraft', status: 'warning' }
        : undefined
    case BiddingApprovalState.PENDING:
      return hasRejectionHistory
        ? { kind: 'priorRejection', status: 'warning' }
        : undefined
    default:
      return undefined
  }
}
